import { getCurrentSettings, setCurrentSettings } from '../services/settings.js?v=83.79';
import {
  CLIENT_LAYOUT_ALLOWED_SIZES,
  CLIENT_LAYOUT_BLOCKS,
  DEFAULT_CLIENT_LAYOUT_CONFIG,
  applyHeroTopSectionToOrder,
  buildClientLayoutFromPreset,
  deriveHeroTopSection,
  sanitizeClientLayoutConfig
} from './client-layout-config.js?v=83.79';

const STORAGE_KEY = 'spressjoy_client_layout_preset';
const FALLBACK_DISPLAY = {
  menu: 'flex',
  review: 'flex',
  social: 'block',
  promo: 'flex'
};
const BLOCK_LABELS = {
  menu: 'Menu',
  review: 'Review',
  social: 'Social',
  promo: 'Promo',
  campaign: 'Campaign',
  rules: 'Rules'
};

const blockMeta = {
  menu: { rootId: 'layoutBlockMenu', targetId: 'locationMenuBtn' },
  review: { rootId: 'layoutBlockReview', targetId: 'feedbackBtn' },
  social: { rootId: 'layoutBlockSocial', targetId: 'socialLinksGroup' },
  promo: { rootId: 'layoutBlockPromo', targetId: 'clientPromoGalleryTrigger' },
  campaign: { rootId: 'layoutBlockCampaign', targetId: 'playBtn' },
  rules: { rootId: 'layoutBlockRules', targetSelector: '#layoutBlockRules > button[data-action="showAutoRules"]' }
};

const activePreview = { presetName: null };

function hydratePreviewPresetFromStorage() {
  if (activePreview.presetName) return;
  const remembered = localStorage.getItem(STORAGE_KEY);
  if (remembered && ['restaurant_promo', 'salon_elegant', 'kids_playful'].includes(remembered)) {
    activePreview.presetName = remembered;
  }
}

function getBlockNodes(blockId) {
  const meta = blockMeta[blockId];
  if (!meta) return null;
  const root = document.getElementById(meta.rootId);
  const target = meta.targetId ? document.getElementById(meta.targetId) : document.querySelector(meta.targetSelector);
  if (!root || !target) return null;
  return { root, target };
}

function syncPresetSummary() {
  const summary = document.getElementById('clientLayoutPresetSummary');
  if (!summary) return;
  summary.textContent = activePreview.presetName
    ? `Preset activ: ${activePreview.presetName}`
    : 'Preset activ: implicit';
}

function cloneConfig(config) {
  return JSON.parse(JSON.stringify(config));
}

function refreshClientPreview() {
  import('./slideshow.js?v=83.79').then((m) => m.applyClientUI()).catch(() => {});
}

function setLayoutConfig(config, { clearPreset = false, rerenderEditor = false } = {}) {
  const sanitized = sanitizeClientLayoutConfig(config);
  const settings = getCurrentSettings() || {};
  setCurrentSettings({ ...settings, clientLayoutConfig: sanitized });
  if (clearPreset) {
    activePreview.presetName = null;
    localStorage.removeItem(STORAGE_KEY);
    syncPresetSummary();
  }
  if (rerenderEditor) {
    renderClientLayoutManualEditor();
  }
  refreshClientPreview();
}

export function getClientLayoutConfig(settings = getCurrentSettings()) {
  hydratePreviewPresetFromStorage();
  if (activePreview.presetName) {
    return buildClientLayoutFromPreset(activePreview.presetName);
  }
  return sanitizeClientLayoutConfig(settings?.clientLayoutConfig);
}

function applyPremiumHeroSectionOrder(config) {
  const hero = document.getElementById('clientHeroActions');
  const promoSection = document.getElementById('clientPremiumSectionPromo');
  const duoSection = document.getElementById('clientPremiumSectionDuo');
  if (!hero || !promoSection || !duoSection) return;

  const anchor =
    document.getElementById('layoutBlockSocial') ||
    document.getElementById('layoutBlockLoyalty') ||
    document.getElementById('layoutBlockEvent') ||
    null;

  const top = config?.heroTopSection === 'duo' ? 'duo' : 'promo';
  if (top === 'duo') {
    if (anchor) {
      hero.insertBefore(promoSection, anchor);
      hero.insertBefore(duoSection, promoSection);
    } else {
      hero.appendChild(duoSection);
      hero.appendChild(promoSection);
    }
  } else if (anchor) {
    hero.insertBefore(duoSection, anchor);
    hero.insertBefore(promoSection, duoSection);
  } else {
    hero.appendChild(promoSection);
    hero.appendChild(duoSection);
  }
}

export function setHeroTopSection(heroTopSection, { persist = true } = {}) {
  const settings = getCurrentSettings() || {};
  const base = sanitizeClientLayoutConfig(settings?.clientLayoutConfig || {});
  const top = heroTopSection === 'duo' ? 'duo' : 'promo';
  const next = {
    ...base,
    heroTopSection: top,
    order: applyHeroTopSectionToOrder(base.order, top)
  };
  if (persist) {
    setLayoutConfig(next, { clearPreset: true, rerenderEditor: true });
  }
  applyClientLayoutConfig({ ...settings, clientLayoutConfig: next });
  return next;
}

function getSelfManagedLayoutBlocks() {
  // Connect / Alfa / Custom își gestionează singure oferta/campania —
  // blocul promo generic („Ofertele săptămânii") nu trebuie afișat de layout renderer.
  const page = document.getElementById('clientPage');
  if (!page) return null;
  if (page.classList.contains('template-custom')) {
    return ['promo', 'campaign', 'rules', 'social', 'loyalty', 'event'];
  }
  if (page.classList.contains('template-connect') || page.classList.contains('template-alfa')) {
    return ['promo'];
  }
  if (page.classList.contains('template-avva')) {
    /* Avva folosește triggerul generic — nu ascunde promo aici; Connect chrome e ascuns separat. */
    return null;
  }
  return null;
}

export function applyClientLayoutConfig(settings = getCurrentSettings(), availability = {}) {
  const selfManaged = getSelfManagedLayoutBlocks();
  if (selfManaged) {
    availability = { ...availability };
    selfManaged.forEach((id) => {
      availability[id] = false;
    });
  }
  const config = getClientLayoutConfig(settings);
  const heroTopSection =
    config.heroTopSection === 'duo' || config.heroTopSection === 'promo'
      ? config.heroTopSection
      : deriveHeroTopSection(config.order);
  const layoutConfig = {
    ...config,
    heroTopSection,
    order: applyHeroTopSectionToOrder(config.order, heroTopSection)
  };

  applyPremiumHeroSectionOrder(layoutConfig);

  layoutConfig.order.forEach((blockId, idx) => {
    const nodes = getBlockNodes(blockId);
    if (!nodes) return;
    const { root, target } = nodes;
    const available = availability[blockId] !== false;
    const visibleByConfig = layoutConfig.visible[blockId] !== false;
    const shouldShow = available && visibleByConfig;

    root.style.order = String(idx + 1);
    root.dataset.layoutSize = layoutConfig.sizes[blockId] || 'md';
    root.dataset.layoutStyle = layoutConfig.style[blockId] || 'glass';
    root.dataset.layoutHighlight = layoutConfig.highlight[blockId] ? '1' : '0';
    root.classList.toggle('layout-highlight', layoutConfig.highlight[blockId] === true);
    if (shouldShow) {
      root.style.removeProperty('display');
    } else {
      root.style.setProperty('display', 'none', 'important');
    }
    if (blockId === 'campaign' || blockId === 'rules') {
      const x = Number(layoutConfig?.offsetX?.[blockId] || 0);
      const safeOffset = Number.isFinite(x) ? Math.max(-160, Math.min(160, x)) : 0;
      root.style.setProperty('--layout-offset-x', `${Math.round(safeOffset)}px`);
    }
    if (FALLBACK_DISPLAY[blockId]) {
      if (shouldShow) {
        target.style.removeProperty('display');
        target.style.display = FALLBACK_DISPLAY[blockId];
      } else {
        target.style.setProperty('display', 'none', 'important');
      }
    }
  });
}

function renderClientLayoutManualEditor() {
  const host = document.getElementById('clientLayoutManualEditorList');
  if (!host) return;
  host.replaceChildren();

  const cfg = getClientLayoutConfig(getCurrentSettings());
  const order = Array.isArray(cfg.order) ? cfg.order : [...CLIENT_LAYOUT_BLOCKS];

  const heroRow = document.createElement('div');
  heroRow.className = 'client-layout-editor-item';
  const heroLabel = document.createElement('div');
  heroLabel.className = 'client-layout-editor-item__name';
  heroLabel.textContent = 'Secțiune sus';
  const heroActions = document.createElement('div');
  heroActions.className = 'client-layout-editor-item__actions';
  const curHero = cfg.heroTopSection || deriveHeroTopSection(cfg.order);
  ['promo', 'duo'].forEach((key) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn-ghost';
    btn.textContent = key === 'promo' ? 'Oferte' : 'Meniu+Recenzie';
    btn.disabled = curHero === key;
    btn.addEventListener('click', () => {
      setHeroTopSection(key);
      renderClientLayoutManualEditor();
    });
    heroActions.appendChild(btn);
  });
  heroRow.appendChild(heroLabel);
  heroRow.appendChild(heroActions);
  host.appendChild(heroRow);

  const applyManualUpdate = (updater) => {
    const next = cloneConfig(cfg);
    updater(next);
    setLayoutConfig(next, { clearPreset: true, rerenderEditor: true });
  };

  order.forEach((blockId, idx) => {
    const row = document.createElement('div');
    row.className = 'client-layout-editor-item';

    const name = document.createElement('div');
    name.className = 'client-layout-editor-item__name';
    name.textContent = BLOCK_LABELS[blockId] || blockId;

    const actions = document.createElement('div');
    actions.className = 'client-layout-editor-item__actions';

    const moveUp = document.createElement('button');
    moveUp.type = 'button';
    moveUp.className = 'btn-ghost';
    moveUp.textContent = '↑';
    moveUp.disabled = idx === 0;
    moveUp.addEventListener('click', () => {
      applyManualUpdate((next) => {
        const i = next.order.indexOf(blockId);
        if (i <= 0) return;
        const x = next.order[i - 1];
        next.order[i - 1] = next.order[i];
        next.order[i] = x;
      });
    });

    const moveDown = document.createElement('button');
    moveDown.type = 'button';
    moveDown.className = 'btn-ghost';
    moveDown.textContent = '↓';
    moveDown.disabled = idx >= order.length - 1;
    moveDown.addEventListener('click', () => {
      applyManualUpdate((next) => {
        const i = next.order.indexOf(blockId);
        if (i < 0 || i >= next.order.length - 1) return;
        const x = next.order[i + 1];
        next.order[i + 1] = next.order[i];
        next.order[i] = x;
      });
    });

    const visibleWrap = document.createElement('label');
    visibleWrap.className = 'client-layout-editor-item__toggle';
    const visibleChk = document.createElement('input');
    visibleChk.type = 'checkbox';
    visibleChk.checked = cfg.visible[blockId] !== false;
    const lockedVisible = blockId === 'campaign' || blockId === 'rules';
    visibleChk.disabled = lockedVisible;
    visibleChk.addEventListener('change', () => {
      applyManualUpdate((next) => {
        next.visible[blockId] = visibleChk.checked;
      });
    });
    const visibleTxt = document.createElement('span');
    visibleTxt.textContent = lockedVisible ? 'visible (lock)' : 'visible';
    visibleWrap.appendChild(visibleChk);
    visibleWrap.appendChild(visibleTxt);

    const sizeSel = document.createElement('select');
    sizeSel.className = 'client-layout-editor-item__select';
    CLIENT_LAYOUT_ALLOWED_SIZES.forEach((sz) => {
      const opt = document.createElement('option');
      opt.value = sz;
      opt.textContent = sz;
      if ((cfg.sizes[blockId] || 'md') === sz) opt.selected = true;
      sizeSel.appendChild(opt);
    });
    sizeSel.addEventListener('change', () => {
      applyManualUpdate((next) => {
        next.sizes[blockId] = sizeSel.value;
      });
    });

    const hiWrap = document.createElement('label');
    hiWrap.className = 'client-layout-editor-item__toggle';
    const hiChk = document.createElement('input');
    hiChk.type = 'checkbox';
    hiChk.checked = cfg.highlight[blockId] === true;
    hiChk.addEventListener('change', () => {
      applyManualUpdate((next) => {
        next.highlight[blockId] = hiChk.checked;
      });
    });
    const hiTxt = document.createElement('span');
    hiTxt.textContent = 'highlight';
    hiWrap.appendChild(hiChk);
    hiWrap.appendChild(hiTxt);

    actions.appendChild(moveUp);
    actions.appendChild(moveDown);
    actions.appendChild(visibleWrap);
    actions.appendChild(sizeSel);
    actions.appendChild(hiWrap);

    row.appendChild(name);
    row.appendChild(actions);
    host.appendChild(row);
  });
}

function applyPresetName(presetName) {
  activePreview.presetName = presetName || null;
  if (presetName) {
    localStorage.setItem(STORAGE_KEY, presetName);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  syncPresetSummary();
  if (presetName) {
    setLayoutConfig(buildClientLayoutFromPreset(presetName), { clearPreset: false, rerenderEditor: true });
  } else {
    setLayoutConfig(DEFAULT_CLIENT_LAYOUT_CONFIG, { clearPreset: true, rerenderEditor: true });
  }
}

export function initClientLayoutPresetControls() {
  const btnRestaurant = document.getElementById('layoutPresetRestaurantBtn');
  const btnSalon = document.getElementById('layoutPresetSalonBtn');
  const btnKids = document.getElementById('layoutPresetKidsBtn');
  const btnReset = document.getElementById('layoutPresetResetBtn');

  const remembered = localStorage.getItem(STORAGE_KEY);
  activePreview.presetName =
    remembered && ['restaurant_promo', 'salon_elegant', 'kids_playful'].includes(remembered)
      ? remembered
      : null;
  syncPresetSummary();
  renderClientLayoutManualEditor();

  if (btnRestaurant && !btnRestaurant.dataset.layoutPresetBound) {
    btnRestaurant.dataset.layoutPresetBound = '1';
    btnRestaurant.addEventListener('click', () => {
      applyPresetName('restaurant_promo');
    });
  }
  if (btnSalon && !btnSalon.dataset.layoutPresetBound) {
    btnSalon.dataset.layoutPresetBound = '1';
    btnSalon.addEventListener('click', () => {
      applyPresetName('salon_elegant');
    });
  }
  if (btnKids && !btnKids.dataset.layoutPresetBound) {
    btnKids.dataset.layoutPresetBound = '1';
    btnKids.addEventListener('click', () => {
      applyPresetName('kids_playful');
    });
  }
  if (btnReset && !btnReset.dataset.layoutPresetBound) {
    btnReset.dataset.layoutPresetBound = '1';
    btnReset.addEventListener('click', () => {
      applyPresetName(null);
    });
  }
}

export function applyExternalLayoutProposal(config) {
  setLayoutConfig(config, { clearPreset: true, rerenderEditor: true });
}
